import React, { useState, useEffect } from 'react';
import { 
    Container, Title, Card, Group, Text, Button, Table, Badge, 
    LoadingOverlay, ScrollArea, Alert 
} from '@mantine/core';
// CHANGED: Replaced IconDatabaseRestore with IconDatabaseImport
import { IconCloudUpload, IconDatabaseImport, IconRefresh, IconAlertCircle } from '@tabler/icons-react';
import axios from '../api/axios';
import Swal from 'sweetalert2';
import Layout from '../common/components/Navbar'; // Importing Layout from Navbar file as per your structure

const Backup = () => {
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const user = JSON.parse(localStorage.getItem('user'));

    const config = {
        headers: {
            Authorization: `Bearer ${user?.token}`,
        },
    };

    // Fetch Backups
    const fetchBackups = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get('/api/backup/list', config);
            setBackups(data);
        } catch (error) {
            console.error("Error fetching backups", error);
            Swal.fire('Error', 'Failed to load backups', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBackups();
        // eslint-disable-next-line
    }, []);

    // Create Backup Handler
    const handleCreateBackup = async () => {
        setActionLoading(true);
        try {
            await axios.post('/api/backup/create', {}, config);
            Swal.fire({
                icon: 'success',
                title: 'Backup Created',
                text: 'The database has been backed up to Google Cloud Storage.',
                timer: 2000
            });
            fetchBackups(); // Refresh list
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Backup failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // Restore Handler
    const handleRestore = async (fileName) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This will OVERWRITE current data with this backup! This cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Restore Database'
        });

        if (result.isConfirmed) {
            setActionLoading(true);
            try {
                await axios.post('/api/backup/restore', { fileName }, config);
                Swal.fire('Restored!', 'Database has been restored successfully.', 'success');
            } catch (error) {
                Swal.fire('Restore Failed', error.response?.data?.message || 'An error occurred', 'error');
            } finally {
                setActionLoading(false);
            }
        }
    };

    // Table Rows
    const rows = backups.map((file) => (
        <Table.Tr key={file.name}>
            <Table.Td>
                <Text size="sm" fw={500}>{file.name}</Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm">{(parseInt(file.size) / 1024 / 1024).toFixed(2)} MB</Text>
            </Table.Td>
            <Table.Td>
                <Badge color="blue" variant="light">
                    {new Date(file.timeCreated).toLocaleString()}
                </Badge>
            </Table.Td>
            <Table.Td>
                <Button 
                    color="red" 
                    variant="subtle" 
                    size="xs" 
                    // CHANGED: Using IconDatabaseImport here
                    leftSection={<IconDatabaseImport size={14} />}
                    onClick={() => handleRestore(file.name)}
                    loading={actionLoading}
                >
                    Restore
                </Button>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <Layout>
            <Container fluid p="md">
                <LoadingOverlay visible={actionLoading} overlayProps={{ radius: "sm", blur: 2 }} />
                
                <Group justify="space-between" mb="lg">
                    <div>
                        <Title order={2} c="green.8">System Backup & Restore</Title>
                        <Text c="dimmed" size="sm">Manage database backups securely via Google Cloud Storage</Text>
                    </div>
                    <Group>
                        <Button 
                            variant="outline" 
                            leftSection={<IconRefresh size={16}/>} 
                            onClick={fetchBackups}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                        <Button 
                            color="green" 
                            leftSection={<IconCloudUpload size={16}/>} 
                            onClick={handleCreateBackup}
                            loading={actionLoading}
                        >
                            Create New Backup
                        </Button>
                    </Group>
                </Group>

                <Alert icon={<IconAlertCircle size={16} />} title="Important Note" color="orange" mb="md">
                    Restoring a backup will replace all current data. Please ensure you have a recent backup of the current state before restoring an older version.
                </Alert>

                <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Card.Section withBorder inheritPadding py="xs">
                        <Text fw={500}>Available Backups (GCS)</Text>
                    </Card.Section>
                    
                    <ScrollArea h={400} mt="sm">
                        <Table striped highlightOnHover verticalSpacing="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>File Name</Table.Th>
                                    <Table.Th>Size</Table.Th>
                                    <Table.Th>Date Created</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {loading ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={4} align="center">
                                            <Text size="sm" c="dimmed" py="xl">Loading backups...</Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : backups.length > 0 ? (
                                    rows
                                ) : (
                                    <Table.Tr>
                                        <Table.Td colSpan={4} align="center">
                                            <Text size="sm" c="dimmed" py="xl">No backups found in cloud storage.</Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Card>
            </Container>
        </Layout>
    );
};

export default Backup;